import React from "react";
import { Tag, Tooltip } from "antd";
import {
  BlockOutlined,
  LinkOutlined,
  DisconnectOutlined,
  DeleteOutlined,
  WarningOutlined,
  EditOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import "./styles.scss";

const stepTypeLabels = {
  click_on_element: "Click on element",
  click_on_random_element: "Click on random element from list",
  collect_dl_object: "Collect dataLayer object",
  collect_dl_before_navigation: "Collect dataLayer before navigation",
  date_range_select: "Select date range",
  fill_input_field: "Fill input field",
  go_to_page: "Go to page",
  scroll_to_percentage: "Scroll to depth",
  select_option: "Select dropdown menu option",
  wait_until_element_visible: "Wait until element appear",
};

const StepBlockWrapper = ({
  block,
  resolvedSteps = [],
  startIndex = 1,
  isStale = false,
  liveName = null,
  onDetach = () => {},
  onDelete = () => {},
  onEditSource = () => {},
}) => {
  const isComponent = block.blockType === "component_ref";
  const isLink = block.blockType === "scenario_link";

  // Prefer the live name from the source so renames propagate; fall back to
  // the snapshot stored on the block (used when stale, i.e. source deleted).
  const storedName = isComponent
    ? block.componentName
    : block.sourceScenarioTitle;
  const blockName =
    (liveName != null ? liveName : storedName) ||
    (isComponent ? "Unnamed Component" : "Unnamed Scenario");

  const blockTypeLabel = isComponent ? "Component" : "Live Link";
  const tagColor = isComponent ? "blue" : "green";
  const wrapperClass = isStale
    ? "stale-ref"
    : isComponent
      ? "component-ref"
      : "scenario-link";

  // Sortable hook — the block is a single draggable unit
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: block.blockId,
    });

  const style = {
    transform: CSS.Transform.toString(transform ? { ...transform, scaleX: 1, scaleY: 1 } : null),
    transition: transition || undefined,
  };

  const handleDetachClick = (e) => {
    e.stopPropagation();
    onDetach(block.blockId);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete(block.blockId);
  };

  const handleEditSourceClick = (e) => {
    e.stopPropagation();
    onEditSource(block);
  };

  const getStepSummary = (step, index) => {
    const label = stepTypeLabels[step?.type] || step?.type || "Unknown";
    const detail = step?.selector || step?.value || "";
    return (
      <div className="step-block-step-row" key={step?.stepId || index}>
        <strong>({startIndex + index})</strong> {label}
        {detail ? ` — ${detail}` : ""}
      </div>
    );
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      className={`step-block-wrapper ${wrapperClass}`}
    >
      <div className="step-block-header">
        <div className="step-block-title">
          {isComponent ? <BlockOutlined /> : <LinkOutlined />}
          <span>{blockName}</span>
          <Tag color={tagColor} className="block-type-tag">
            {blockTypeLabel}
          </Tag>
        </div>
        <div className="step-block-actions">
          {!isStale && (
            <Tooltip title={isComponent ? "Edit component" : "Open scenario"}>
              {isComponent ? (
                <EditOutlined
                  onClick={handleEditSourceClick}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <ExportOutlined
                  onClick={handleEditSourceClick}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ cursor: "pointer" }}
                />
              )}
            </Tooltip>
          )}
          <Tooltip title="Detach — keep steps but unlink from source">
            <DisconnectOutlined
              onClick={handleDetachClick}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ cursor: "pointer" }}
            />
          </Tooltip>
          <Tooltip title={`Delete ${blockTypeLabel.toLowerCase()}`}>
            <DeleteOutlined
              onClick={handleDeleteClick}
              onPointerDown={(e) => e.stopPropagation()}
              style={{ cursor: "pointer" }}
            />
          </Tooltip>
        </div>
      </div>

      <div className="step-block-body">
        {resolvedSteps.length > 0 ? (
          resolvedSteps.map((step, idx) => getStepSummary(step, idx))
        ) : (
          <div className="step-block-empty">No steps defined</div>
        )}
      </div>

      {isStale && (
        <div className="step-block-warning">
          <WarningOutlined style={{ marginRight: 6 }} />
          {isComponent
            ? "Component not found — detach or delete this block."
            : "Source scenario not found — detach or delete this block."}
        </div>
      )}
    </div>
  );
};

export { StepBlockWrapper };
export default StepBlockWrapper;
